import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Package, User, Truck, FileText, Save, Search, Plus, MapPin, Printer } from 'lucide-react';
import apiClient from '../services/api';
import { printConsignment } from '../utils/printUtils';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';
const CONSIGNMENT_TYPE = 'LT';

const ConsignmentEntryFormLT = ({ onClose, onSave, editData = null }) => {
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState('');
  const [employees,        setEmployees]        = useState([]);
  const [drivers,          setDrivers]          = useState([]);
  const [customers,        setCustomers]        = useState([]);
  const [vehicles,         setVehicles]         = useState([]);
  const [cities,           setCities]           = useState([]);
  const [countries,        setCountries]        = useState([]);
  const [containerTypes,   setContainerTypes]   = useState([]);
  const [unitMasters,      setUnitMasters]      = useState([]);
  const [paymentModes,     setPaymentModes]     = useState([]);
  const [generatingNumber, setGeneratingNumber] = useState(false);

  const [truckAssignments,    setTruckAssignments]    = useState([]);
  const [allTruckAssignments, setAllTruckAssignments] = useState([]);
  const [selectedAssignmentID, setSelectedAssignmentID] = useState('');

  const [showCustomerModal,   setShowCustomerModal]   = useState(false);
  const [customerModalType,   setCustomerModalType]   = useState('');
  const [customerSearchTerm,  setCustomerSearchTerm]  = useState('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);

  const [shipperStates,   setShipperStates]   = useState([]);
  const [consigneeStates, setConsigneeStates] = useState([]);
  const [newCustomerStates, setNewCustomerStates] = useState([]);
  const [newCustomerCities, setNewCustomerCities] = useState([]);

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };
  const session   = getSessionData();
  const companyId = session?.companyid;

  const emptyForm = {
    ConsignmentNo: '',
    ConsignmentDate: (() => { const now = new Date(); return new Date(now - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16); })(),
    ConsignmentType: CONSIGNMENT_TYPE,
    OriginCityID: '', DestinationCityID: '',
    ShipperID: '', ShipperCode: '', ShipperName: '', ShipperContactPerson: '',
    ShipperFlatBuilding: '', ShipperStreet: '', ShipperCustomerIDNo: '',
    ShipperPhone: '', ShipperWhatsApp: '', ShipperCountryID: '', ShipperStateID: '', ShipperCityID: '',
    ConsigneeID: '', ConsigneeCode: '', ConsigneeName: '', ConsigneeContactPerson: '',
    ConsigneeFlatBuilding: '', ConsigneeStreet: '',
    ConsigneePhone: '', ConsigneeWhatsApp: '', ConsigneeCountryID: '', ConsigneeStateID: '', ConsigneeCityID: '',
    Description: '', NoOfPkgs: '', PackageType: '', Pieces: '', CourierType: '',
    ContainerTypeID: '', ContainerNo: '', ContainerNo2: '',
    Length: '', Width: '', Height: '', Volume: '', VolumeWeight: '', Weight: '',
    InvoiceValue: '', Reference: '', DDCopyBack: false, NCND: false,
    HSNCode: '', TransportationCharge: '', BillingType: '', PaymentMode: '',
    CollectMaterial: false, CashOnly: false, ChequeCash: false, MaterialCost: '',
    CODRequired: false, COCRequired: false, CODAmount: '',
    ProductType: '', DespatchDate: '', VehicleID: '', DriverID: '',
    CollectedBy: '', ReceivedBy: '', Load: 'FTL',
    CourierStatusID: '', DeliveryDate: '', Remarks: '',
    Transit: false, SeparateDocs: false, TruckAssignmentID: '',
    InvoiceToID: '', InvoiceToName: '', InvoiceToCode: '',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [newCustomerData, setNewCustomerData] = useState({
    customername: '', contactperson: '', customertype: 'CR',
    phone: '', whatsappno: '', email: '', address: '',
    countryid: '', stateid: '', cityid: '', taxnumber: '',
  });

  useEffect(() => {
    const init = async () => {
      const allAssignments = await loadDropdownData();
      if (editData?.ConsignmentID) {
        setFormData(prev => ({
          ...prev, ...editData, ConsignmentType: CONSIGNMENT_TYPE,
          ConsignmentDate: (() => {
            const val = editData.ConsignmentDate;
            if (!val || val === '0000-00-00' || val === '0000-00-00 00:00:00') {
              const now = new Date(); return new Date(now - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
            }
            return val.replace(' ', 'T').slice(0, 16);
          })(),
          ConsignmentNo: editData.ConsignmentNo || '',
          ShipperName: editData.ShipperName || '',
          ConsigneeName: editData.ConsigneeName || '',
          TruckAssignmentID: editData.TruckAssignmentID || '',
        }));
        if (editData.TruckAssignmentID) {
          setSelectedAssignmentID(String(editData.TruckAssignmentID));
          const found = allAssignments.find(a => String(a.AssignmentID) === String(editData.TruckAssignmentID));
          if (found) {
            const despatchDate = found.AssignmentDate ? new Date(found.AssignmentDate).toISOString().slice(0, 10) : '';
            setFormData(prev => ({ ...prev, ...editData, ConsignmentType: CONSIGNMENT_TYPE, TruckAssignmentID: editData.TruckAssignmentID, VehicleID: found.VehicleID ? String(found.VehicleID) : (editData.VehicleID || ''), DriverID: found.DriverID ? String(found.DriverID) : (editData.DriverID || ''), DespatchDate: editData.DespatchDate || despatchDate }));
          }
        }
      } else {
        setGeneratingNumber(true);
        await generateConsignmentNumber();
        setGeneratingNumber(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (formData.ShipperCountryID) loadStatesByCountry(formData.ShipperCountryID, 'shipper');
    else { setShipperStates([]); }
  }, [formData.ShipperCountryID]);

  useEffect(() => {
    if (formData.ConsigneeCountryID) loadStatesByCountry(formData.ConsigneeCountryID, 'consignee');
    else { setConsigneeStates([]); }
  }, [formData.ConsigneeCountryID]);

  useEffect(() => {
    if (formData.ShipperName && !formData.InvoiceToID) {
      setFormData(prev => ({ ...prev, InvoiceToID: prev.ShipperID || '__shipper__', InvoiceToName: prev.ShipperName, InvoiceToCode: prev.ShipperCode || '' }));
    }
  }, [formData.ShipperID, formData.ShipperName]);

  const loadDropdownData = async () => {
    try {
      const [empRes, driverRes, custRes, vehRes, cityRes, countryRes, assignRes, ctRes, unitRes, pmRes] = await Promise.all([
        apiClient.get('/Employees.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Drivers.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Customers.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Vehicles.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Cities.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Countries.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/VehicleAssignments.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/LookupMaster.php', { params: { table: 'containertype', active: '1' } }).catch(() => ({ data: { success: false } })),
        apiClient.get('/UnitMaster.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/PaymentModes.php').catch(() => ({ data: { success: false } })),
      ]);
      if (empRes.data.success)     setEmployees(empRes.data.data || []);
      if (custRes.data.success)    setCustomers(custRes.data.data || []);
      if (vehRes.data.success)     setVehicles(vehRes.data.data || []);
      if (cityRes.data.success)    setCities(cityRes.data.data || []);
      if (countryRes.data.success) setCountries(countryRes.data.data || []);
      if (ctRes.data.success)      setContainerTypes(ctRes.data.data || []);
      if (unitRes.data.success)    setUnitMasters(unitRes.data.data || []);
      if (pmRes.data.success)      setPaymentModes(pmRes.data.data || []);
      if (driverRes.data.success)  setDrivers((driverRes.data.data || []).filter(d => parseInt(d.isactive) === 1));
      let allAssignments = [];
      if (assignRes.data.success) {
        allAssignments = assignRes.data.data || [];
        setAllTruckAssignments(allAssignments);
        setTruckAssignments(allAssignments.filter(a => a.Status !== 'Cancelled' && a.Status !== 'Completed'));
      }
      return allAssignments;
    } catch (err) { console.error(err); setError('Failed to load dropdown data'); return []; }
  };

  const loadStatesByCountry = async (countryId, type) => {
    try {
      const res = await apiClient.get('/States.php', { params: { countryid: countryId } });
      if (res.data.success) {
        if (type === 'shipper')       setShipperStates(res.data.data || []);
        else if (type === 'consignee') setConsigneeStates(res.data.data || []);
        else                           setNewCustomerStates(res.data.data || []);
      }
    } catch (e) { console.error(e); }
  };

  const loadCitiesByState = async (stateId, type) => {
    try {
      const res = await apiClient.get('/Cities.php', { params: { stateid: stateId } });
      if (res.data.success) {
        if (type === 'newcustomer') setNewCustomerCities(res.data.data || []);
      }
    } catch (e) { console.error(e); }
  };

  const generateConsignmentNumber = async () => {
    try {
      const res = await apiClient.get(`/ConsignmentsLT.php/next-number?type=${CONSIGNMENT_TYPE}`);
      if (res.data.success && res.data.data?.consignmentNo) setFormData(p => ({ ...p, ConsignmentNo: res.data.data.consignmentNo }));
      else throw new Error('Failed');
    } catch { setFormData(p => ({ ...p, ConsignmentNo: `CNS-LT-${Date.now()}` })); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      const l = parseFloat(name === 'Length' ? value : prev.Length) || 0;
      const w = parseFloat(name === 'Width'  ? value : prev.Width)  || 0;
      const h = parseFloat(name === 'Height' ? value : prev.Height) || 0;
      if (l > 0 && w > 0 && h > 0) {
        const vol = (l * w * h) / 1000000;
        updated.Volume = vol.toFixed(6);
        updated.VolumeWeight = (vol * 167).toFixed(3);
      } else { updated.Volume = ''; updated.VolumeWeight = ''; }
      return updated;
    });
  };

  const handleAssignmentSelect = (assignmentId) => {
    setSelectedAssignmentID(assignmentId);
    if (!assignmentId) { setFormData(p => ({ ...p, VehicleID: '', DriverID: '', TruckAssignmentID: '', DespatchDate: '' })); return; }
    const found = allTruckAssignments.find(a => String(a.AssignmentID) === String(assignmentId));
    if (found) {
      const despatchDate = found.AssignmentDate ? new Date(found.AssignmentDate).toISOString().slice(0, 10) : '';
      setFormData(p => ({ ...p, TruckAssignmentID: assignmentId, VehicleID: found.VehicleID ? String(found.VehicleID) : '', DriverID: found.DriverID ? String(found.DriverID) : '', DespatchDate: despatchDate }));
    }
  };

  const openCustomerModal = (type) => { setCustomerModalType(type); setCustomerSearchTerm(''); setShowAddCustomerForm(false); setShowCustomerModal(true); };

  const selectCustomer = async (customer) => {
    if (customerModalType === 'shipper') {
      setFormData(p => ({ ...p, ShipperID: customer.customerid, ShipperCode: customer.customercode, ShipperName: customer.customername, ShipperContactPerson: customer.contactperson || '', ShipperPhone: customer.phone || '', ShipperWhatsApp: customer.whatsappno || '', ShipperCountryID: customer.countryid || '', ShipperStateID: customer.stateid || '', ShipperCityID: customer.cityid || '', InvoiceToID: p.InvoiceToID || customer.customerid || '__shipper__', InvoiceToName: p.InvoiceToID ? p.InvoiceToName : customer.customername, InvoiceToCode: p.InvoiceToID ? p.InvoiceToCode : (customer.customercode || '') }));
      if (customer.countryid) await loadStatesByCountry(customer.countryid, 'shipper');
    } else {
      setFormData(p => ({ ...p, ConsigneeID: customer.customerid, ConsigneeCode: customer.customercode, ConsigneeName: customer.customername, ConsigneeContactPerson: customer.contactperson || '', ConsigneePhone: customer.phone || '', ConsigneeWhatsApp: customer.whatsappno || '', ConsigneeCountryID: customer.countryid || '', ConsigneeStateID: customer.stateid || '', ConsigneeCityID: customer.cityid || '' }));
      if (customer.countryid) await loadStatesByCountry(customer.countryid, 'consignee');
    }
    setShowCustomerModal(false);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!newCustomerData.customername.trim()) { alert('Customer name is required'); return; }
    try {
      const res = await apiClient.post('/Customers.php', { ...newCustomerData, companyid: companyId, isactive: 1 });
      if (res.data.success) {
        await loadDropdownData();
        selectCustomer({ customerid: res.data.data.customerid, customercode: res.data.data.customercode, customername: newCustomerData.customername, contactperson: newCustomerData.contactperson, phone: newCustomerData.phone, whatsappno: newCustomerData.whatsappno, countryid: newCustomerData.countryid, stateid: newCustomerData.stateid, cityid: newCustomerData.cityid });
        setNewCustomerData({ customername: '', contactperson: '', customertype: 'CR', phone: '', whatsappno: '', email: '', address: '', countryid: '', stateid: '', cityid: '', taxnumber: '' });
        setNewCustomerStates([]); setNewCustomerCities([]);
        setShowAddCustomerForm(false);
        alert(`Customer created!\nCode: ${res.data.data.customercode}`);
      } else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error adding customer: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(formData.ConsignmentNo || '').trim())  { setError('Consignment number is required'); return; }
    if (!(formData.ShipperName || '').trim())    { setError('Shipper name is required');       return; }
    if (!(formData.ConsigneeName || '').trim())  { setError('Consignee name is required');     return; }
    if (!formData.OriginCityID)                  { setError('Origin city is required');        return; }
    if (!formData.DestinationCityID)             { setError('Destination city is required');   return; }
    setLoading(true); setError('');
    try {
      const dataToSave = {
        ...formData, ConsignmentType: 'LT',
        OriginCityID: formData.OriginCityID || null, DestinationCityID: formData.DestinationCityID || null,
        ShipperID: formData.ShipperID || null, ConsigneeID: formData.ConsigneeID || null,
        CollectedBy: formData.CollectedBy || null, ReceivedBy: formData.ReceivedBy || null,
        VehicleID: formData.VehicleID || null, DriverID: formData.DriverID || null,
        CourierStatusID: formData.CourierStatusID || null,
        ShipperCountryID: formData.ShipperCountryID || null, ShipperStateID: formData.ShipperStateID || null, ShipperCityID: formData.ShipperCityID || null,
        ConsigneeCountryID: formData.ConsigneeCountryID || null, ConsigneeStateID: formData.ConsigneeStateID || null, ConsigneeCityID: formData.ConsigneeCityID || null,
        ContainerTypeID: formData.ContainerTypeID || null, TruckAssignmentID: formData.TruckAssignmentID || null,
        InvoiceToID: formData.InvoiceToID || null,
        NoOfPkgs: formData.NoOfPkgs || 0, Pieces: formData.Pieces || 0, Weight: formData.Weight || 0,
        Length: formData.Length || 0, Width: formData.Width || 0, Height: formData.Height || 0,
        Volume: formData.Volume || 0, VolumeWeight: formData.VolumeWeight || 0,
        InvoiceValue: formData.InvoiceValue || 0, TransportationCharge: formData.TransportationCharge || 0,
        MaterialCost: formData.MaterialCost || 0, CODAmount: formData.CODAmount || 0,
        DDCopyBack: formData.DDCopyBack ? 1 : 0, NCND: formData.NCND ? 1 : 0,
        CollectMaterial: formData.CollectMaterial ? 1 : 0, CashOnly: formData.CashOnly ? 1 : 0,
        ChequeCash: formData.ChequeCash ? 1 : 0, CODRequired: formData.CODRequired ? 1 : 0,
        COCRequired: formData.COCRequired ? 1 : 0, Transit: formData.Transit ? 1 : 0,
        SeparateDocs: formData.SeparateDocs ? 1 : 0,
      };
      if (editData?.ConsignmentID) dataToSave.ConsignmentID = editData.ConsignmentID;
      await onSave(dataToSave);
    } catch (err) { setError(err.message || 'Failed to save consignment'); }
    finally { setLoading(false); }
  };

  const filteredCustomers = customers.filter(c =>
    c.customername?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.customercode?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.phone?.includes(customerSearchTerm)
  );

  const assignmentDropdownOptions = (() => {
    if (!editData || !formData.TruckAssignmentID) return truckAssignments;
    const savedId = String(formData.TruckAssignmentID);
    if (truckAssignments.some(a => String(a.AssignmentID) === savedId)) return truckAssignments;
    const saved = allTruckAssignments.find(a => String(a.AssignmentID) === savedId);
    return saved ? [saved, ...truckAssignments] : truckAssignments;
  })();

  // shared styles
  const inpCls  = `w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] font-[inherit]`;
  const numCls  = inpCls + ' text-right font-mono font-semibold tracking-wide';
  const selCls  = inpCls + ' cursor-pointer';
  const lblCls  = 'block mb-1.5 text-sm font-medium text-gray-700';
  const numAutoStyle = { background: '#DCFCE7', fontStyle: 'italic', color: '#16A34A' };

  const SectionTitle = ({ icon: Icon, title, action, style = '' }) => (
    <div className={`flex items-center gap-2.5 mb-4 pb-3 border-b-2 border-gray-100 ${style}`}>
      <Icon size={16} className="text-[#2563EB] flex-shrink-0" />
      <span className="text-[15px] font-semibold text-gray-800 flex-1">{title}</span>
      {action}
    </div>
  );

  const FormSection = ({ children, bg = 'bg-white' }) => (
    <div className={`${bg} rounded-xl p-5 mb-4 border border-[#E2E8F0] shadow-sm`}>{children}</div>
  );

  const SubLabel = ({ text }) => (
    <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3 border-l-4 border-gray-300 pl-2">{text}</div>
  );

  const SelectBtn = ({ type }) => (
    <button type="button" onClick={() => openCustomerModal(type)}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer"
      style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
      <Search size={12} /> Select
    </button>
  );

  const CheckRow = ({ name, label }) => (
    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer select-none">
      <input type="checkbox" name={name} checked={!!formData[name]} onChange={handleChange}
        className="w-4 h-4 rounded accent-[#2563EB]" />
      {label}
    </label>
  );

  // ── Customer Modal (portal) ─────────────────────────────────────────────────
  const customerModalJSX = showCustomerModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}>
      <div className="bg-white rounded-xl w-full max-w-[900px] max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
          <h3 className="text-lg font-semibold m-0" style={{ color: '#fff' }}>
            Select {customerModalType === 'shipper' ? 'Shipper' : 'Consignee'}
          </h3>
          <button onClick={() => setShowCustomerModal(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150"
            style={{ color: '#fff' }}>
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center gap-2 px-3 py-2.5 border-2 border-[#CBD5E1] rounded-lg bg-white mb-4 focus-within:border-[#2563EB] transition-colors duration-150">
            <Search size={18} className="text-gray-400" />
            <input type="text" placeholder="Search by name, code or phone..."
              value={customerSearchTerm} onChange={e => setCustomerSearchTerm(e.target.value)} autoFocus
              className="flex-1 border-0 outline-none text-sm" />
          </div>
          <button type="button" onClick={() => setShowAddCustomerForm(!showAddCustomerForm)}
            className="flex items-center gap-1.5 px-3 py-2 mb-4 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
            style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
            <Plus size={14} /> {showAddCustomerForm ? 'Hide Form' : 'Add New Customer'}
          </button>
          {showAddCustomerForm && (
            <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-[#E2E8F0]">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                <User size={14} className="text-[#2563EB]" /> New Customer
              </div>
              <form onSubmit={handleAddCustomer}>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className={lblCls}>Customer Name *</label><input type="text" value={newCustomerData.customername} onChange={e => setNewCustomerData({ ...newCustomerData, customername: e.target.value })} required className={inpCls} placeholder="Full customer name" /></div>
                  <div><label className={lblCls}>Contact Person</label><input type="text" value={newCustomerData.contactperson} onChange={e => setNewCustomerData({ ...newCustomerData, contactperson: e.target.value })} className={inpCls} placeholder="Contact person name" /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" value={newCustomerData.phone} onChange={e => setNewCustomerData({ ...newCustomerData, phone: e.target.value })} className={inpCls} placeholder="+91 ..." /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="submit" className="px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer flex items-center gap-1" style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}><Plus size={12} /> Create &amp; Select</button>
                  <button type="button" onClick={() => setShowAddCustomerForm(false)} className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-[#CBD5E1] rounded-lg cursor-pointer hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border border-[#E2E8F0] max-h-[380px] overflow-y-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-[#F1F5F9] sticky top-0">
                <tr>{['Code','Name','Contact Person','Phone','Action'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500 border-b border-[#E2E8F0]">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0
                  ? <tr><td colSpan="5" className="py-8 text-center text-gray-400 text-sm">No customers found</td></tr>
                  : filteredCustomers.map(c => (
                    <tr key={c.customerid} className="border-b border-gray-100 hover:bg-[#EFF6FF] transition-colors duration-150">
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{c.customercode}</td>
                      <td className="px-3 py-2.5 font-medium text-gray-800">{c.customername}</td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs">{c.contactperson || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2.5 text-gray-500">{c.phone}</td>
                      <td className="px-3 py-2.5">
                        <button type="button" onClick={() => selectCustomer(c)}
                          className="px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer"
                          style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  if (!companyId) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[9999]">
        <div className="bg-white rounded-xl w-full max-w-[500px] p-8 text-center shadow-2xl">
          <p className="text-gray-600 mb-4">Please select a company and financial year from the dashboard.</p>
          <button onClick={onClose} className="px-4 py-2 text-white border-0 rounded-lg cursor-pointer" style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 overflow-y-auto" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl w-full max-w-[1400px] max-h-[95vh] flex flex-col shadow-2xl" style={{ animation: 'slideIn 0.3s ease-out' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 rounded-t-xl flex-shrink-0 border-b-4"
            style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, borderBottomColor: '#1E40AF' }}>
            <div className="flex items-center gap-6 flex-1">
              <div className="w-14 h-14 rounded-xl bg-white/30 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Truck size={24} color="#fff" />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[1.6rem] font-bold" style={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
                  {editData ? 'Edit' : 'New'} Consignment – Land Transport (LT)
                </span>
                <span className="font-mono text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/40 bg-white/25 whitespace-nowrap" style={{ color: '#fff' }}>
                  📋 {generatingNumber ? 'Generating...' : (formData.ConsignmentNo || 'Loading...')}
                </span>
              </div>
            </div>
            <button onClick={onClose}
              className="w-11 h-11 flex items-center justify-center rounded-xl border-2 border-white/30 bg-white/20 cursor-pointer transition-all duration-150 hover:bg-white/35 hover:rotate-90 flex-shrink-0"
              style={{ color: '#fff' }}>
              <X size={20} color="#fff" />
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-6 py-3 mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium flex-shrink-0">
              <strong>Error:</strong> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 bg-[#F8FAFC]">

              {/* Basic Info */}
              <FormSection>
                <SectionTitle icon={FileText} title="Basic Information (Land Transport)" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lblCls}>Consignment No *</label>
                    <input type="text" name="ConsignmentNo" value={generatingNumber ? 'Loading...' : formData.ConsignmentNo} readOnly
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm font-bold text-[1.05rem] outline-none ${generatingNumber ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-[#EFF6FF] text-gray-900 border-[#93C5FD]'}`} />
                  </div>
                  <div>
                    <label className={lblCls}>Consignment Date & Time *</label>
                    <input type="datetime-local" name="ConsignmentDate" value={formData.ConsignmentDate} onChange={handleChange} required className={inpCls} />
                  </div>
                </div>
              </FormSection>

              {/* Route */}
              <FormSection>
                <SectionTitle icon={MapPin} title="Consignment Route" />
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={lblCls}>Origin (Pickup City) *</label>
                    <select name="OriginCityID" value={formData.OriginCityID} onChange={handleChange} required className={selCls}>
                      <option value="">-- Select Origin City --</option>
                      {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname} ({c.statename})</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Destination (Delivery City) *</label>
                    <select name="DestinationCityID" value={formData.DestinationCityID} onChange={handleChange} required className={selCls}>
                      <option value="">-- Select Destination City --</option>
                      {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname} ({c.statename})</option>)}
                    </select>
                  </div>
                </div>
              </FormSection>

              {/* Shipper */}
              <FormSection>
                <SectionTitle icon={User} title="Shipper | Consignor" action={<SelectBtn type="shipper" />} />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                  <div><label className={lblCls}>Customer Code</label><input type="text" name="ShipperCode" value={formData.ShipperCode} readOnly className={inpCls + ' bg-gray-100 cursor-not-allowed'} /></div>
                  <div><label className={lblCls}>Name *</label><input type="text" name="ShipperName" value={formData.ShipperName} onChange={handleChange} required className={inpCls} /></div>
                  <div><label className={lblCls}>Contact Person</label><input type="text" name="ShipperContactPerson" value={formData.ShipperContactPerson} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" name="ShipperPhone" value={formData.ShipperPhone} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>WhatsApp</label><input type="text" name="ShipperWhatsApp" value={formData.ShipperWhatsApp} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Country</label>
                    <select name="ShipperCountryID" value={formData.ShipperCountryID} onChange={e => { handleChange(e); setFormData(p => ({ ...p, ShipperCountryID: e.target.value, ShipperStateID: '', ShipperCityID: '' })); }} className={selCls}>
                      <option value="">Select Country</option>
                      {countries.map(c => <option key={c.countryid} value={c.countryid}>{c.countryname}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>State</label>
                    <select name="ShipperStateID" value={formData.ShipperStateID} onChange={e => { handleChange(e); setFormData(p => ({ ...p, ShipperStateID: e.target.value, ShipperCityID: '' })); }} className={selCls}>
                      <option value="">Select State</option>
                      {shipperStates.map(s => <option key={s.stateid} value={s.stateid}>{s.statename}</option>)}
                    </select>
                  </div>
                </div>
              </FormSection>

              {/* Consignee */}
              <FormSection>
                <SectionTitle icon={User} title="Receiver | Consignee" action={<SelectBtn type="consignee" />} />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                  <div><label className={lblCls}>Customer Code</label><input type="text" name="ConsigneeCode" value={formData.ConsigneeCode} readOnly className={inpCls + ' bg-gray-100 cursor-not-allowed'} /></div>
                  <div><label className={lblCls}>Name *</label><input type="text" name="ConsigneeName" value={formData.ConsigneeName} onChange={handleChange} required className={inpCls} /></div>
                  <div><label className={lblCls}>Contact Person</label><input type="text" name="ConsigneeContactPerson" value={formData.ConsigneeContactPerson} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" name="ConsigneePhone" value={formData.ConsigneePhone} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>WhatsApp</label><input type="text" name="ConsigneeWhatsApp" value={formData.ConsigneeWhatsApp} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Country</label>
                    <select name="ConsigneeCountryID" value={formData.ConsigneeCountryID} onChange={e => { handleChange(e); setFormData(p => ({ ...p, ConsigneeCountryID: e.target.value, ConsigneeStateID: '', ConsigneeCityID: '' })); }} className={selCls}>
                      <option value="">Select Country</option>
                      {countries.map(c => <option key={c.countryid} value={c.countryid}>{c.countryname}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>State</label>
                    <select name="ConsigneeStateID" value={formData.ConsigneeStateID} onChange={e => { handleChange(e); setFormData(p => ({ ...p, ConsigneeStateID: e.target.value, ConsigneeCityID: '' })); }} className={selCls}>
                      <option value="">Select State</option>
                      {consigneeStates.map(s => <option key={s.stateid} value={s.stateid}>{s.statename}</option>)}
                    </select>
                  </div>
                </div>
              </FormSection>

              {/* Package Details */}
              <FormSection>
                <SectionTitle icon={Package} title="Package Details" />

                {/* 1. Package Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-[#E2E8F0]">
                  <SubLabel text="1. Package Summary" />
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                    <div className="col-span-full"><label className={lblCls}>Package Description</label><textarea name="Description" value={formData.Description} onChange={handleChange} rows={2} className={`${inpCls} resize-y min-h-[60px]`} /></div>
                    <div><label className={lblCls}>No. of Packages</label><input type="number" name="NoOfPkgs" value={formData.NoOfPkgs} onChange={handleChange} min="0" className={numCls} /></div>
                    <div><label className={lblCls}>Package Type</label>
                      <select name="PackageType" value={formData.PackageType} onChange={handleChange} className={selCls}>
                        <option value="">Select Type</option>
                        {unitMasters.filter(u => parseInt(u.IsActive) === 1).map(u => <option key={u.ItemUnitID} value={u.UnitCode}>{u.ItemUnit} ({u.UnitCode})</option>)}
                      </select>
                    </div>
                    <div><label className={lblCls}>Container Type</label>
                      <select name="ContainerTypeID" value={formData.ContainerTypeID} onChange={handleChange} className={selCls}>
                        <option value="">-- Select Container Type --</option>
                        {containerTypes.map(ct => <option key={ct.ContainerTypeID} value={ct.ContainerTypeID}>{ct.ContainerType}</option>)}
                      </select>
                    </div>
                    <div><label className={lblCls}>Container No. 1</label><input type="text" name="ContainerNo" value={formData.ContainerNo} onChange={handleChange} placeholder="e.g. TCKU1234567" className={inpCls} /></div>
                    <div><label className={lblCls}>Container No. 2</label><input type="text" name="ContainerNo2" value={formData.ContainerNo2} onChange={handleChange} placeholder="e.g. MSCU7654321" className={inpCls} /></div>
                  </div>
                </div>

                {/* 2. Dimensions */}
                <div className="bg-emerald-50 rounded-lg p-4 mb-4 border border-emerald-200">
                  <SubLabel text="2. Dimensions & Volume" />
                  <div className="grid grid-cols-3 gap-4">
                    <div><label className={lblCls}>Length (cm)</label><input type="number" step="0.01" name="Length" value={formData.Length} onChange={handleDimensionChange} min="0" placeholder="0.00" className={numCls} /></div>
                    <div><label className={lblCls}>Width (cm)</label><input type="number" step="0.01" name="Width" value={formData.Width} onChange={handleDimensionChange} min="0" placeholder="0.00" className={numCls} /></div>
                    <div><label className={lblCls}>Height (cm)</label><input type="number" step="0.01" name="Height" value={formData.Height} onChange={handleDimensionChange} min="0" placeholder="0.00" className={numCls} /></div>
                    <div><label className={lblCls}>Volume (m³) <span className="text-[10px] text-[#16A34A] font-normal">auto</span></label>
                      <input type="number" step="0.000001" name="Volume" value={formData.Volume} onChange={handleChange} min="0" placeholder="L×W×H ÷ 1,000,000" className={numCls} style={numAutoStyle} /></div>
                    <div><label className={lblCls}>Volume Weight (kg) <span className="text-[10px] text-[#16A34A] font-normal">auto</span></label>
                      <input type="number" step="0.001" name="VolumeWeight" value={formData.VolumeWeight} onChange={handleChange} min="0" placeholder="Volume × 167" className={numCls} style={numAutoStyle} /></div>
                    <div><label className={lblCls}>Actual Weight (kg)</label><input type="number" step="0.001" name="Weight" value={formData.Weight} onChange={handleChange} min="0" placeholder="0.000" className={numCls} /></div>
                  </div>
                </div>

                {/* 3. Value */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <SubLabel text="3. Value & Documents" />
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                    <div><label className={lblCls}>Invoice Value</label><input type="number" step="0.01" name="InvoiceValue" value={formData.InvoiceValue} onChange={handleChange} min="0" placeholder="0.00" className={numCls} /></div>
                    <div><label className={lblCls}>Invoice Copy / Ref No.</label><input type="text" name="Reference" value={formData.Reference} onChange={handleChange} placeholder="Invoice number or reference" className={inpCls} /></div>
                  </div>
                </div>
              </FormSection>

              {/* Payment & Collection */}
              <FormSection>
                <SectionTitle icon={FileText} title="Payment & Collection Details" />

                <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-[#E2E8F0]">
                  <SubLabel text="1. Tax / Codes" />
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                    <div><label className={lblCls}>HSN Code</label><input type="text" name="HSNCode" value={formData.HSNCode} onChange={handleChange} placeholder="HSN Code" className={inpCls} /></div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <SubLabel text="2. Charges & Billing" />
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                    <div><label className={lblCls}>Transportation Charge</label><input type="number" step="0.01" name="TransportationCharge" value={formData.TransportationCharge} onChange={handleChange} min="0" placeholder="0.00" className={numCls} /></div>
                    <div><label className={lblCls}>Billing Type</label>
                      <select name="BillingType" value={formData.BillingType} onChange={handleChange} className={selCls}>
                        <option value="">Select</option>
                        <option value="To-Pay">To-Pay</option>
                        <option value="Paid">Paid</option>
                        <option value="Credit">Credit</option>
                      </select>
                    </div>
                    <div className="col-span-full">
                      <label className={lblCls}>Invoice To</label>
                      <div className="flex gap-2 items-start">
                        <select name="InvoiceToID" value={formData.InvoiceToID}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === '__shipper__') setFormData(p => ({ ...p, InvoiceToID: p.ShipperID || '__shipper__', InvoiceToName: p.ShipperName || '', InvoiceToCode: p.ShipperCode || '' }));
                            else if (val === '__consignee__') setFormData(p => ({ ...p, InvoiceToID: p.ConsigneeID || '__consignee__', InvoiceToName: p.ConsigneeName || '', InvoiceToCode: p.ConsigneeCode || '' }));
                            else { const found = customers.find(c => String(c.customerid) === String(val)); setFormData(p => ({ ...p, InvoiceToID: val, InvoiceToName: found?.customername || '', InvoiceToCode: found?.customercode || '' })); }
                          }}
                          className={selCls + ' flex-1'}>
                          <option value="">-- Select Invoice To --</option>
                          <option value="__shipper__">Consignor / Shipper{formData.ShipperName ? ` — ${formData.ShipperName}` : ''}</option>
                          <option value="__consignee__">Consignee{formData.ConsigneeName ? ` — ${formData.ConsigneeName}` : ''}</option>
                          <optgroup label="Other Customers">
                            {customers.filter(c => String(c.customerid) !== String(formData.ShipperID) && String(c.customerid) !== String(formData.ConsigneeID)).map(c => (
                              <option key={c.customerid} value={c.customerid}>{c.customercode} — {c.customername}</option>
                            ))}
                          </optgroup>
                        </select>
                        {formData.InvoiceToName && (
                          <div className="flex items-center px-3 py-2.5 bg-[#EFF6FF] border border-[#93C5FD] rounded-lg text-sm text-[#1D4ED8] whitespace-nowrap">
                            {formData.InvoiceToCode && <span className="font-mono mr-1.5 text-xs">{formData.InvoiceToCode}</span>}
                            {formData.InvoiceToName}
                          </div>
                        )}
                      </div>
                    </div>
                    <div><label className={lblCls}>Payment Mode</label>
                      <select name="PaymentMode" value={formData.PaymentMode} onChange={handleChange} className={selCls}>
                        <option value="">Select</option>
                        {paymentModes.map(pm => <option key={pm.paymentmodeid} value={pm.paymentmodecode}>{pm.paymentmodename}</option>)}
                      </select>
                    </div>
                    <div><label className={lblCls}>Material Cost Amount</label><input type="number" step="0.01" name="MaterialCost" value={formData.MaterialCost} onChange={handleChange} min="0" placeholder="0.00" className={numCls} /></div>
                    <div className="flex items-center"><CheckRow name="CollectMaterial" label="Material Cost to Collect" /></div>
                  </div>
                </div>
              </FormSection>

              {/* Truck Details */}
              <FormSection>
                <SectionTitle icon={Truck} title="Truck Details" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                  <div className="col-span-full">
                    <label className={lblCls + ' flex items-center gap-1.5'}>
                      <Truck size={13} className="text-amber-500" />
                      Select Truck Assignment
                      <span className="text-[11px] font-normal text-gray-500 ml-1">(auto-fills Vehicle &amp; Driver)</span>
                    </label>
                    <select value={selectedAssignmentID} onChange={e => handleAssignmentSelect(e.target.value)}
                      className={`${selCls} ${selectedAssignmentID ? 'border-amber-400 bg-amber-50' : ''}`}>
                      <option value="">-- Select Truck Assignment --</option>
                      {assignmentDropdownOptions.map(a => {
                        const date   = a.AssignmentDate ? new Date(a.AssignmentDate).toLocaleDateString() : '';
                        const origin = a.OriginCity || a.Origin || '';
                        const dest   = a.DestinationCity || a.Destination || '';
                        const driver = a.DriverName || '';
                        const statusTag = (a.Status === 'Completed' || a.Status === 'Cancelled') ? ` [${a.Status}]` : '';
                        return (
                          <option key={a.AssignmentID} value={a.AssignmentID}>
                            {a.TripDocNo || `TD-${String(a.AssignmentID).padStart(6,'0')}`}
                            {date ? ` | ${date}` : ''}{origin ? ` | ${origin}` : ''}{dest ? ` → ${dest}` : ''}{driver ? ` | ${driver}` : ''}{statusTag}
                          </option>
                        );
                      })}
                    </select>
                    {truckAssignments.length === 0 && <p className="text-[12px] text-gray-400 mt-1">No active truck assignments found</p>}
                  </div>
                  <div><label className={lblCls}>Despatch Date</label><input type="date" name="DespatchDate" value={formData.DespatchDate} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Vehicle</label>
                    <select name="VehicleID" value={formData.VehicleID} onChange={handleChange} className={selCls}>
                      <option value="">Select Vehicle</option>
                      {vehicles.map(v => <option key={v.VehicleID} value={v.VehicleID}>{v.RegistrationNo}{v.VehicleType && ` - ${v.VehicleType}`}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Driver {drivers.length === 0 && '(No active drivers)'}</label>
                    <select name="DriverID" value={formData.DriverID} onChange={handleChange} className={selCls}>
                      <option value="">Select Driver</option>
                      {drivers.map(d => <option key={d.driverid} value={d.driverid}>{d.drivername}{d.phone && ` - ${d.phone}`}{d.licenseno && ` | License: ${d.licenseno}`}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Load Type</label>
                    <select name="Load" value={formData.Load} onChange={handleChange} className={selCls}>
                      <option value="FTL">FTL (Full Truck Load)</option>
                      <option value="LTL">LTL (Less Than Truck Load)</option>
                    </select>
                  </div>
                </div>
              </FormSection>

              {/* Delivery Details */}
              <FormSection>
                <SectionTitle icon={FileText} title="Delivery Details" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                  <div><label className={lblCls}>Courier Status</label>
                    <select name="CourierStatusID" value={formData.CourierStatusID} onChange={handleChange} className={selCls}>
                      <option value="">Select Status</option>
                      <option value="1">Pending</option><option value="2">In Transit</option>
                      <option value="3">Delivered</option><option value="4">Returned</option>
                    </select>
                  </div>
                  <div><label className={lblCls}>Date</label><input type="date" name="DeliveryDate" value={formData.DeliveryDate} onChange={handleChange} className={inpCls} /></div>
                  <div className="col-span-full"><label className={lblCls}>Remarks</label><textarea name="Remarks" value={formData.Remarks} onChange={handleChange} rows={2} className={`${inpCls} resize-y min-h-[60px]`} /></div>
                </div>
              </FormSection>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] rounded-b-xl flex-shrink-0">
              <button type="button" onClick={onClose} disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-600 bg-white border-2 border-[#CBD5E1] rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed">
                Cancel
              </button>
              {editData && (
                <button type="button" onClick={() => printConsignment(formData, CONSIGNMENT_TYPE)} disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg,#16A34A,#15803D)' }}>
                  <Printer size={16} /> Print Consignment
                </button>
              )}
              <button type="submit" disabled={loading || generatingNumber}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(37,99,235,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                {loading
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
                  : <><Save size={16} />Save Land Transport Consignment</>}
              </button>
            </div>
          </form>
        </div>
      </div>

      {customerModalJSX}
      <style>{`@keyframes slideIn { from { opacity:0; transform:translateY(-20px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </>
  );
};

export default ConsignmentEntryFormLT;